import {Subscription} from "rxjs";
import {NgTemplateOutlet} from "@angular/common";
import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Params, Router, RouterLink} from "@angular/router";

import {
  AdCardComponent,
  AdCardData, defaultPageLimit,
  PaginationComponent,
  SearchInputComponent,
  SearchResultComponent,
  SidebarMenuComponent,
  SidebarMenuItem,
  SortOption
} from "@numizmat/components";

import {appRoutes} from "@numizmat/constants";
import {environment} from "@numizmat/environments";
import {AdListApiService} from "@numizmat/services/core";
import {RestoreScrollPositionDirective} from "@numizmat/directives";
import {JsonLdService, MetaTagService} from "@numizmat/services/common";
import {AdListSortOptionApiService, AdListSortOptionStateService} from "@numizmat/services/nomenclature";
import {MainAdCategoryApiService} from "@numizmat/services/core/main-ad-category/main-ad-category-api.service";
import {
  FilterAdListRequest,
  FilterMainAdCategoryRequest,
  FilterMainAdCategoryResponse
} from "@numizmat/services-models/core";
import {AdListFilterComponent} from "@numizmat/pages/ad-list-page/components/ad-list-filter/ad-list-filter.component";

@Component({
    selector: 'nz-ad-list-page',
    imports: [
        RouterLink,
        AdCardComponent,
        SidebarMenuComponent,
        SearchResultComponent,
        SearchInputComponent,
        PaginationComponent,
        RestoreScrollPositionDirective,
        AdListFilterComponent,
        NgTemplateOutlet
    ],
    templateUrl: './ad-list-page.component.html',
    styleUrl: './ad-list-page.component.scss'
})
export class AdListPageComponent implements OnInit, OnDestroy {
  private router: Router = inject(Router);
  private jsonLdService: JsonLdService = inject(JsonLdService);
  private activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private metaTagService: MetaTagService = inject(MetaTagService);
  private adListApiService: AdListApiService = inject(AdListApiService);
  private mainAdCategoryApiService: MainAdCategoryApiService = inject(MainAdCategoryApiService);
  private adListSortOptionApiService: AdListSortOptionApiService = inject(AdListSortOptionApiService);
  private adListSortOptionStateService: AdListSortOptionStateService = inject(AdListSortOptionStateService);

  private sellerId: string = '';
  private adCategoryId: number = 0;
  private adSubcategoryId: number = 0;
  private subs: Subscription = new Subscription();
  private mainAdCategories: FilterMainAdCategoryResponse[] = [];

  protected loading: boolean = false;
  protected currentRoute: string = '';
  protected searchValue: string = '';
  protected sellerUsername: string = '';
  protected totalResults: number = 0;
  protected selectedPage: number = 1;
  protected selectedSortOption: number = 1;
  protected selectedPageLimitOptionId: number = defaultPageLimit;
  protected selectedCategoryMenuItem?: SidebarMenuItem;
  protected selectedSubcategoryMenuItem?: SidebarMenuItem;

  protected filteredAds: AdCardData[] = [];
  protected sortOptions: SortOption[] = [];
  protected categoryMenuItems: SidebarMenuItem[] = [];
  protected subcategoryMenuItems: SidebarMenuItem[] = [];

  protected readonly defaultPageLimit = defaultPageLimit;

  public ngOnInit(): void {
    this.currentRoute = this.router.url.split('?')[0];
    this.getAllAdListSortOptions();
    this.createQueryParamsChangeSubscription();
    this.metaTagService.setCanonicalUrl(`${environment.applicationDomain}/${appRoutes.adList}`);
  }

  public ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.jsonLdService.removeSchema();
  }

  protected onSort(sortOption: number): void {
    this.router.navigate([this.currentRoute], {
      queryParamsHandling: 'merge',
      queryParams:
        {sortOption: sortOption, page: 1},
    }).then();
  }

  protected onSearch(searchValue: string): void {
    this.router.navigate([this.currentRoute], {
      queryParamsHandling: 'merge',
      queryParams:
        {searchValue: searchValue, page: 1},
    }).then();
  }

  private getAllAdListSortOptions(): void {
    const subscription: Subscription = this.adListSortOptionStateService.getAllAdListSortOptions().subscribe({
      next: adListSortOptions => {
        if (!!adListSortOptions) {
          this.sortOptions = adListSortOptions.map(x => {
            return {
              id: x.adListSortOption,
              name: x.adListSortOptionName
            }
          });
        } else {
          this.adListSortOptionApiService.getAllAdListSortOptions().subscribe({
            next: response => {
              this.adListSortOptionStateService.setAllAdListSortOptions(response);
            }
          });
        }
      }
    });
    this.subs.add(subscription);
  }

  private createQueryParamsChangeSubscription(): void {
    const subscription: Subscription = this.activatedRoute.queryParams.subscribe({
      next: (queryParams: Params) => {
        this.adCategoryId = !!queryParams['adCategoryId'] ? Number.parseInt(queryParams['adCategoryId']) : 0;
        this.adSubcategoryId = !!queryParams['adSubcategoryId'] ? Number.parseInt(queryParams['adSubcategoryId']) : 0;
        this.selectedSortOption = !!queryParams['sortOption'] ? Number.parseInt(queryParams['sortOption']) : 1;
        this.selectedPage = !!queryParams['page'] ? Number.parseInt(queryParams['page']) : 1;
        this.selectedPageLimitOptionId = !!queryParams['pageLimit'] ? Number.parseInt(queryParams['pageLimit']) : defaultPageLimit;

        const sellerId = queryParams['sellerId'];
        const sellerUsername = queryParams['sellerUsername'];
        const searchValue = queryParams['searchValue'];

        if (this.mainAdCategories.length === 0 || this.sellerId !== sellerId || searchValue !== this.searchValue) {
          this.sellerId = sellerId;
          this.sellerUsername = sellerUsername;
          this.searchValue = searchValue;
          this.filterMainAdCategories();
        } else {
          this.sellerId = sellerId;
          this.sellerUsername = sellerUsername;
          this.searchValue = searchValue;
          this.setSelectedCategory();
        }
        this.filterAds();
        this.setMetaTags();
      }
    });
    this.subs.add(subscription);
  }

  private filterAds(): void {
    this.loading = true;
    const queryParams = this.activatedRoute.snapshot.queryParams;
    const selectedAdType: number = !!queryParams['adType'] ? Number.parseInt(queryParams['adType']) : 0;
    const selectedAdSpecificationOptionIds: number[] = [];
    const adSpecSin: string[] = !!queryParams['adSpecSin'] ? queryParams['adSpecSin'].split('|') : [];
    adSpecSin.forEach(adSpec => {
      const adSpecification = adSpec.split('_');
      selectedAdSpecificationOptionIds.push(+adSpecification[1]);
    });
    const adSpecMul: string[] = !!queryParams['adSpecMul'] ? queryParams['adSpecMul'].split('|') : [];
    adSpecMul.forEach(adSpec => {
      const adSpecification = adSpec.split('_');
      selectedAdSpecificationOptionIds.push(...adSpecification[1].split(',').map(x => Number.parseInt(x)));
    });
    const request: FilterAdListRequest = {
      searchValue: this.searchValue,
      adCategoryId: this.adCategoryId,
      adSubcategoryId: this.adSubcategoryId,
      sellerId: !!this.sellerId ? this.sellerId : undefined,
      selectedPage: this.selectedPage,
      selectedPageLimit: this.selectedPageLimitOptionId,
      selectedSortOption: this.selectedSortOption,
      selectedAdType: selectedAdType,
      selectedAdSpecificationOptionIds: selectedAdSpecificationOptionIds
    };
    this.adListApiService.filterAdList(request).subscribe({
      next: response => {
        this.loading = false;
        this.totalResults = response.totalResults;
        this.filteredAds = response.ads.map(x => {
          return {
            adId: x.adId,
            title: x.title,
            imageUrl: `${environment.apiImageAdUrl}/main/${x.adId}`,
            price: x.price,
            adType: x.adType,
            expiryDate: x.endDateTime,
            userAdLikeId: x.userAdLikeId,
            additionalImageIds: x.additionalImageIds,
          }
        });
      }
    });
    this.setSchema();
  }

  private filterMainAdCategories(): void {
    const request: FilterMainAdCategoryRequest = {
      searchValue: this.searchValue,
      sellerId: !!this.sellerId ? this.sellerId : undefined,
      adCategoryId: this.adCategoryId ? this.adCategoryId : undefined,
    };
    this.mainAdCategoryApiService.filterMainAdCategories(request).subscribe({
      next: response => {
        this.mainAdCategories = response;
        this.setMetaTags();
        this.categoryMenuItems = this.mainAdCategories.map(x => {
          return {
            id: x.adCategoryId,
            title: x.adCategoryName,
            badge: x.adCount,
            queryParams: {
              adCategoryId: x.adCategoryId,
              adSubcategoryId: null,
              page: 1,
              adType: null,
              adSpecSin: null,
              adSpecMul: null
            },
            routerLink: [appRoutes.root, appRoutes.adList]
          }
        });
        this.categoryMenuItems.unshift({
          id: 0,
          title: 'Всички',
          badge: this.mainAdCategories.map(x => x.adCount)
            .reduce((accumulator, currentValue) => {
              return accumulator + currentValue
            }, 0),
          queryParams: {
            adCategoryId: null,
            adSubcategoryId: null,
            page: 1,
            adType: null,
            adSpecSin: null,
            adSpecMul: null
          },
          routerLink: [appRoutes.root, appRoutes.adList]
        });
        this.setSelectedCategory();
      }
    });
  }

  private setSelectedCategory(): void {
    const adCategoryIndex = this.categoryMenuItems.findIndex(x => x.id === this.adCategoryId);
    this.selectedCategoryMenuItem = this.categoryMenuItems[adCategoryIndex];
    const mainAdCategoryIndex = this.mainAdCategories.findIndex(x => x.adCategoryId === this.adCategoryId);

    if (mainAdCategoryIndex < 0 || this.mainAdCategories[mainAdCategoryIndex].subcategories.length === 0) {
      this.subcategoryMenuItems = [];
      return;
    }

    this.subcategoryMenuItems = this.mainAdCategories[mainAdCategoryIndex]?.subcategories.map(x => {
      return {
        id: x.adSubcategoryId,
        title: x.adSubcategoryName,
        badge: x.adCount,
        queryParams: {adSubcategoryId: x.adSubcategoryId, page: 1, adType: null, adSpecSin: null, adSpecMul: null},
        routerLink: [appRoutes.root, appRoutes.adList]
      }
    });

    this.subcategoryMenuItems.unshift({
      id: 0,
      title: 'Всички',
      badge: this.mainAdCategories[mainAdCategoryIndex]?.subcategories.map(x => x.adCount)
        .reduce((accumulator, currentValue) => {
          return accumulator + currentValue
        }, 0),
      queryParams: {adSubcategoryId: null, page: 1, adType: null, adSpecSin: null, adSpecMul: null},
      routerLink: [appRoutes.root, appRoutes.adList]
    });

    const adSubcategoryId = this.subcategoryMenuItems.findIndex(x => x.id === this.adSubcategoryId);
    this.selectedSubcategoryMenuItem = this.subcategoryMenuItems[adSubcategoryId];
  }

  private setSchema(): void {
    const categoryName = this.selectedCategoryMenuItem?.title || 'Всички';
    const subcategoryName = this.selectedSubcategoryMenuItem?.title || null;

    const adSchema = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "Списък с обяви",
      "description": "Обяви за колекционерски артикули, включително монети, банкноти и марки.",
      "mainEntity": {
        "@type": "ItemList",
        "name": "Списък с обяви",
        "itemListElement": this.filteredAds.map((ad, index) => ({
          "@type": "Product",
          "position": index + 1,
          "name": ad.title,
          "image": [ad.imageUrl],
          "description": ad.title,
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": 5,
            "bestRating": 5,
            "ratingCount": 100
          },
          "review": [
            {
              "@type": "Review",
              "author": {"@type": "Person", "name": "Системен потребител"},
              "reviewRating": {
                "@type": "Rating",
                "ratingValue": 5
              }
            }
          ],
          "offers": {
            "@type": "Offer",
            "price": ad.price,
            "priceCurrency": "BGN",
            "url": `${environment.applicationDomain}/ad/${ad.adId}`,
            "availability": "https://schema.org/InStock",
            "hasMerchantReturnPolicy": {
              "@type": "MerchantReturnPolicy",
              "name": "Връщане не е разрешено",
              "applicableCountry": "BG",
              "returnPolicyCategory": "https://schema.org/MerchantReturnNotPermitted"
            },
            "shippingDetails": {
              "@type": "OfferShippingDetails",
              "shippingDestination": {
                "@type": "DefinedRegion",
                "addressCountry": "BG"
              },
              "shippingRate": {
                "@type": "MonetaryAmount",
                "currency": "BGN",
                "value": 0
              },
              "deliveryTime": {
                "@type": "ShippingDeliveryTime",
                "handlingTime": {
                  "@type": "QuantitativeValue",
                  "minValue": 2,
                  "maxValue": 5,
                  "unitCode": "DAY"
                },
                "transitTime": {
                  "@type": "QuantitativeValue",
                  "minValue": 2,
                  "maxValue": 5,
                  "unitCode": "DAY"
                }
              },
              "description": "Съгласно общите условия на сайта и условията посочени от продавача."
            },
            "priceValidUntil": ad.expiryDate
          }
        }))
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Начало",
            "item": `${environment.applicationDomain}`
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": categoryName,
            "item": `${environment.applicationDomain}/ad-list?adCategoryId=${this.adCategoryId}`
          },
          ...(subcategoryName
            ? [
              {
                "@type": "ListItem",
                "position": 3,
                "name": subcategoryName,
                "item": `${environment.applicationDomain}/ad-list?adCategoryId=${this.adCategoryId}&adSubcategoryId=${this.adSubcategoryId}`
              }
            ]
            : [])
        ]
      }
    };

    this.jsonLdService.setSchema(adSchema);
  }

  private setMetaTags(): void {
    let title = 'Всички обяви | Collectoria';
    let description = 'Всички обяви | Collectoria';

    if (!!this.sellerUsername) {
      title = `Обяви на '${this.sellerUsername}' | Collectoria`;
      description = `Разгледай всички обяви на '${this.sellerUsername}' в Collectoria. Колекционерски артикули – монети, банкноти, марки и други редки предмети.`;
    } else if (this.searchValue) {
      title = `Обяви за '${this.searchValue}' | Collectoria`;
      description = `Намерете обяви за '${this.searchValue}' в Collectoria. Купете редки колекционерски предмети на страхотни цени!`;
    } else if (this.adSubcategoryId && this.adSubcategoryId !== 0) {
      const adCategoryName = this.mainAdCategories
        .find(x => x.adCategoryId === this.adCategoryId)?.adCategoryName;
      const adSubcategoryName = this.mainAdCategories
        .find(x => x.adCategoryId === this.adCategoryId)?.subcategories
        .find(x => x.adSubcategoryId === this.adSubcategoryId)?.adSubcategoryName;
      title = `${adCategoryName} | ${adSubcategoryName} | Collectoria`;
      description = `Разгледайте категорията '${adCategoryName} > ${adSubcategoryName}' в Collectoria. Открийте редки и интересни колекционерски артикули.`;
    } else if (this.adCategoryId && this.adCategoryId !== 0) {
      const adCategoryName = this.mainAdCategories
        .find(x => x.adCategoryId === this.adCategoryId)?.adCategoryName;
      title = `${adCategoryName} | Collectoria`;
      description = `Разгледайте всички обяви в категория '${adCategoryName}' в Collectoria. Колекционерски артикули, монети, банкноти и редки предмети.`;
    }

    this.metaTagService.setTitle(title);
    this.metaTagService.setDescription(description);
  }
}
